import type { CoreSelect } from '@pollux-docaposte/core-design-system/dist/types/components/core-select/core-select'
import { Component, Host, h, Element, Prop, Event, type EventEmitter, State } from '@stencil/core'

@Component({
  tag: 'cobalt-select',
  styleUrl: 'cobalt-select.css'
})
export class CobaltSelect {
  /**
   * The label that accompany the select.
   */
  @Prop({ mutable: true }) label!: string
  /**
   * Description is available on `outside` and `aside` format and will append below the label.
   */
  @Prop({ mutable: true }) description?: string
  /**
   * Placeholder inside the select.
   */
  @Prop({ mutable: true }) placeholder?: string = 'Select an option'
  /**
   * Mention displayed to indicate if the field is mandatory or optional. If none is selected `custom-requirement` slot is available.
   */
  @Prop({ mutable: true }) requirement?: 'mandatory' | 'optional' | 'none' = 'mandatory'
  /**
   * Array of objects describing the options. It should be in strict JSON format.
   *
   * The array could be a simple list of options:
   * ```
   * const options = [
   *  {
   *    "value": string,
   *    "disabled": boolean // can be omitted if not true
   *  }
   *  ...
   * ]
   * ```
   * Or it could be splitted in multiple sections:
   * ```
   * const options = [
   *  {
   *    "title": string, // title of the section
   *    "options": [
   *      {
   *        "value": string,
   *        "disabled": boolean // can be omitted if not true
   *      }
   *      ...
   *    ]
   *  }
   *  ...
   * ]
   * ```
   */
  @Prop({ mutable: true }) options: CoreSelect['options'] = []
  /**
   * Position the label in different place.
   */
  @Prop({ mutable: true }) format?: 'inside' | 'outside' | 'aside' | 'hidden' = 'inside'
  /**
   * A boolean indicating the disable state of the Select field. The `aria-disabled` attribute relies on this property.
   */
  @Prop({ mutable: true }) disabled?: boolean = false
  /**
   * Display an error message under the field and set the field in error state.
   */
  @Prop({ mutable: true }) errorMessage?: string = null

  /**
   * Corresponding to the option selected in the format `{ value: string, disabled: boolean }`
   */
  @State() selectedOption: string
  /**
   * Corresponding to a unique index of the option.
   */
  @State() selectedOptionIndex: number | string

  @Element() element: HTMLElement

  /**
   * Emitted when the value has changed.
   */
  @Event() cobaltSelectOptionChange: EventEmitter<string>

  selectOption = (option: string): void => {
    this.selectedOption = option
    this.cobaltSelectOptionChange.emit(option)
  }

  render(): any {
    return (
      <Host>
        <core-select
          label={this.label}
          description={this.description}
          placeholder={this.placeholder}
          requirement={this.requirement}
          format={this.format}
          options={this.options}
          disabled={this.disabled}
          errorMessage={this.errorMessage}
          onCoreSelectOptionChange={event => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            this.selectOption(event.detail as string)
          }}
        >
          {this.requirement === 'none' && <slot slot="custom-requirement" name="custom-requirement" />}
        </core-select>
      </Host>
    )
  }
}
